import axios, { type AxiosResponse } from 'axios'
import {debounce} from 'lodash-es'
import { ElMessage } from 'element-plus'
import router from '@/router' // 直接导入router实例
import { type ApiReturnType } from '../types/common'
import { sessionStorageManager } from '@/lib/utils'

// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
axios.defaults.headers.post['Content-Type'] = 'application/json'

export const businessServerAxiosInstance = axios.create({
  timeout: 180000,
  withCredentials: true // Check Cross-Site Access-Control
})

businessServerAxiosInstance.interceptors.request.use(
  (config) => {
    // 从sessionStorageManager获取token并添加到请求头
    const token = sessionStorageManager.getToken();
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error)
  }
)

businessServerAxiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.status === 200 || response.status === 201) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return response.data
    } else {
      return Promise.reject(response.data)
    }
  },
  (error) => {
    console.log(111, error)
    toastFunc({
      type: 'error',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
      message: error.msg || error.message || '网络请求错误'
    })
    return Promise.reject(error)
  }
)


const toastFunc = debounce(errorToast, 300)

function checkStatus<R = unknown>(response: AxiosResponse<ApiReturnType<R>>) {
  if (response.status === 403 && response.config.url === '/api/plan/info') return
  if (!response) return
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
  if ([200, 201].includes(response.status)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return response
  }
  if (response.status === 401) {
    ElMessage({
      type: 'warning',
      message: '登录已失效，请重新登录'
    })
    router.replace({
      name: 'login',
      query: {
        redirect: encodeURIComponent(router.currentRoute.value.fullPath),
      }
    })
    throw new Error('登录已失效，请重新登录')
    // return {
    //   statusCode: 401,
    //   message: '登录已失效，请重新登录',
    //   success: false,
    //   data: null
    // }
  }
  let message = null
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  switch (response.status) {
    case 404:
      message = '访问路径不存在'
      break
    case 500:
      message = '服务器内部错误'
      break
    case 502:
      message = '网关错误'
      break
    case 504:
      message = '网关超时'
      break
    default:
      message = response.statusText
  }
  ElMessage({
    type: 'error',
    message: message
  })
  throw new Error(message)
  // const {data, ...others} = response
  // return {
  //   ...others,
  //   data: {
  //     statusCode: response.status,
  //     message: message || response.statusText,
  //     data: response.statusText,
  //     success: false
  //   }
  // } as unknown as AxiosResponse<ApiReturnType<R>>
}

function checkCode<R = unknown>(response?: AxiosResponse<ApiReturnType<R>>) {
  if (!response) {
    ElMessage({
      type: 'error',
      message: '请求失败'
    })
    return
  }
  // 检查是否是文件下载请求
  const disposition = response.headers['content-disposition'];
  const contentType = response.headers['content-type'];

  // 判断是否是 blob
  if (
    disposition ||
    (contentType && contentType.includes('application/octet-stream'))
  ) {
    // 这里的 response.data 是 Blob
    const fileName = disposition
      ? decodeURIComponent(disposition.split('filename=')[1]?.replace(/['"]/g, '') || 'download')
      : 'download';

    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return Promise.resolve('Download initiated');
  }
  const res = response.data
  if (!res) {
    ElMessage({
      type: 'error',
      message: '请求失败'
    })
    return
  }
  // 登录失效
  if (res.statusCode === 10001) {
    ElMessage({
      type: 'warning',
      message: '登录已过期，请重新登录'
    })
    router.replace({
      name: 'login',
      query: {
        redirect: encodeURIComponent(router.currentRoute.value.fullPath),
      }
    })
    return 
  }
  // 提示
  if (!res.success) {
    const message = res.message ?? '服务出错请联系管理员'
    ElMessage({
      type: 'warning',
      message: message
    })
    return null
  }
  return res.data;
}

function errorToast(res: any) {
  ElMessage(res)
}

export const ajax = {
  post<R = any, TData = any, TParams = any>(url: string, data: TData, params?: TParams, headers?: Record<string, string>, configs?: Record<string, string>): Promise<R> {
    // 从sessionStorageManager获取token
    const token = sessionStorageManager.getToken();
    const authHeaders = {
      ...headers,
      'Authorization': token ? `Bearer ${token}` : undefined
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    // @ts-ignore
    return axios({
      method: 'post',
      url,
      data: data,
      timeout: 180000,
      params,
      headers: authHeaders,
      ...(configs || {})
    })
      .then(checkCode)
      .catch(e => {
        return checkStatus(e)
      })
  },
  put<R = any, TData = any, TParams = any>(url: string, data: TData, params?: TParams): Promise<R> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    // @ts-ignore
    return axios({
      method: 'put',
      url,
      data: data,
      timeout: 60000,
      params
    })
    .then(checkCode)
    .catch(e => {
      return checkStatus(e)
    })
  },
  upload<R = any, TData = any>(url: string, data: TData): Promise<R> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    // @ts-ignore
    return axios({
      method: 'post',
      url,
      data: data,
      timeout: 180000
    }).then(checkCode)
    .catch(e => {
      return checkStatus(e)
    })
  },
  get<R = any, TData = any, TParams = any>(url: string, data?: TData, params?: TParams, headers?: Record<string, string>, configs?: Record<string, string>): Promise<R> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    // @ts-ignore
    // 从sessionStorageManager获取token
    const token = sessionStorageManager.getToken();
    const authHeaders = {
      ...headers,
      'Authorization': token ? `Bearer ${token}` : undefined
    };
    // @ts-ignore
    return axios({
      method: 'get',
      url,
      data: data,
      timeout: 180000,
      params,
      headers: authHeaders,
      ...(configs || {})
    })
      .then(checkCode)
      .catch(e => {
        return checkStatus(e)
      })
  },
  delete<R = any, TData = any, TParams = any>(url: string, data?: TData, params?: TParams): Promise<R> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    // @ts-ignore
    return axios({
      method: 'delete',
      url,
      data: data,
      timeout: 60000,
      params
    })
    .then(checkCode)
    .catch(e => {
      return checkStatus(e)
    })
  }
}
export default ajax
